import type {ActionReducerMapBuilder} from '@reduxjs/toolkit';
import {getDefaultOrdersLazyData} from '@Components/event-ticketing/event-ticketing.types';
import type {TicketingOrdersBatch, TicketingOrdersListLazyData} from '@Components/event-ticketing/event-ticketing.types';
import {cancelTicketingAttendees, fetchTicketingDashboard, refundTicketOrder} from '@Components/event-ticketing/event-ticketing-thunk';
import {LoadingStates} from '@Utils/loading.util';
import {addTicketingDonor, fetchTicketingDonations} from './event-donations-thunk';
import {getActiveDonationsList} from './event-donations.helper';
import type {EventDonationsState, FetchTicketingDonationsArgs} from './event-donations.types';

/**
 * Lands a page in its list: the first page replaces whatever the list holds (a re-read after a refund keeps the old
 * rows on screen until it lands, see rereadDonationsListsHolding), a later page appends, skipping ids already present.
 */
const appendDonationsPageToList = (list: TicketingOrdersListLazyData, batch: TicketingOrdersBatch): void => {
  const knownOrderIds = new Set(batch.currentPageNumber === 1 ? [] : list.orderIds);
  const newOrderIds = batch.orders.map((order) => order.orderId).filter((orderId) => !knownOrderIds.has(orderId));
  list.orderIds = batch.currentPageNumber === 1 ? newOrderIds : list.orderIds.concat(newOrderIds);
  list.currentPageNumber = batch.currentPageNumber;
  list.lastPage = batch.lastPage;
  list.total = batch.total;
  list.loadingState = LoadingStates.LOADED;
};

/**
 * True when a page no longer belongs in the view: it was fetched for another event (the organiser switched events
 * while it was in flight) or for a search they have since changed.
 */
const isDonationsResponseOutdated = (state: EventDonationsState, arg: FetchTicketingDonationsArgs): boolean => {
  return arg.eventHashedID !== state.eventHashedID || arg.search !== state.search;
};

/**
 * Re-reads, from page 1, each list the order is on. A refund or cancel can hand the donation back, and only the
 * backend knows whether it did (the row summary keeps the amount the donor gave), so the list is fetched again rather
 * than pruned here. Its rows stay on screen meanwhile; the first page replaces them when it lands.
 */
const rereadDonationsListsHolding = (state: EventDonationsState, orderId: number): void => {
  [state.defaultDonations, state.filteredDonations]
    .filter((list) => list.orderIds.includes(orderId))
    .forEach((list) => {
      list.currentPageNumber = 0;
      list.lastPage = 1;
      list.loadingState = LoadingStates.NOT_LOADED;
    });
};

export const eventDonationsExtraReducers = (builder: ActionReducerMapBuilder<EventDonationsState>): void => {
  // The rows live in the ticketing slice's orders hashmap, which every dashboard load clears — so the id lists here
  // are dropped with it, whichever event is being opened, and the tab lazy-loads its first page afresh.
  builder.addCase(fetchTicketingDashboard.pending, (state, {meta}) => {
    state.defaultDonations = getDefaultOrdersLazyData();
    state.filteredDonations = getDefaultOrdersLazyData();
    state.search = '';
    state.liveSearchTerm = '';
    state.eventHashedID = meta.arg;
    state.hasDonationsFetchFailed = false;
  });

  builder.addCase(fetchTicketingDonations.pending, (state, {meta}) => {
    state.eventHashedID = meta.arg.eventHashedID;
    state.hasDonationsFetchFailed = false;
    getActiveDonationsList(state, meta.arg.search).loadingState = LoadingStates.LOADING;
  });

  builder.addCase(fetchTicketingDonations.fulfilled, (state, {meta, payload}) => {
    if (isDonationsResponseOutdated(state, meta.arg)) {
      return;
    }
    appendDonationsPageToList(getActiveDonationsList(state, meta.arg.search), payload);
  });

  // An aborted page (the tab unmounted) re-arms the list so the next visit fetches it again. A failed one re-arms it
  // too but raises the error flag, which holds the lazy load off — no retry loop — until the search changes.
  builder.addCase(fetchTicketingDonations.rejected, (state, {meta}) => {
    if (isDonationsResponseOutdated(state, meta.arg)) {
      return;
    }
    getActiveDonationsList(state, meta.arg.search).loadingState = LoadingStates.NOT_LOADED;
    state.hasDonationsFetchFailed = !meta.aborted;
  });

  builder.addCase(refundTicketOrder.fulfilled, (state, {meta}) => {
    rereadDonationsListsHolding(state, meta.arg.orderId);
  });

  builder.addCase(cancelTicketingAttendees.fulfilled, (state, {meta}) => {
    rereadDonationsListsHolding(state, meta.arg.orderId);
  });

  // A hand-recorded donor is a NEW order: put it at the top of the unfiltered list (it cannot be known to match an
  // active search, so it surfaces there once the search is cleared).
  builder.addCase(addTicketingDonor.fulfilled, (state, {payload}) => {
    const isNewOrder = !state.defaultDonations.orderIds.includes(payload.orderId);
    state.defaultDonations.orderIds = [payload.orderId, ...state.defaultDonations.orderIds.filter((orderId) => orderId !== payload.orderId)];
    if (isNewOrder) {
      state.defaultDonations.total += 1;
    }
  });
};
